import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Trash2, Pencil, History, ExternalLink, Search, UploadCloud } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { FormDialog } from '@/components/ui/form-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api, { openStoredObject } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import toast from 'react-hot-toast';

interface DocumentItem {
  id: string;
  groupId: string;
  title: string;
  description: string | null;
  category: string;
  entityType: string | null;
  entityId: string | null;
  tags: string | null;
  fileUrl: string;
  fileName: string | null;
  mimeType: string | null;
  fileSize: number | null;
  version: number;
  isLatest: boolean;
  uploadedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DocumentListResponse {
  data: DocumentItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const CATEGORIES = [
  'CNIC',
  'CONTRACT',
  'INVOICE',
  'RECEIPT',
  'LEGAL',
  'WARRANTY',
  'UTILITY_BILL',
  'SALARY_SLIP',
  'OTHER',
] as const;

const ENTITY_TYPES = ['CUSTOMER', 'GUARANTOR', 'CONTRACT', 'PRODUCT', 'SUPPLIER', 'SALE', 'OTHER'] as const;

const ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,application/pdf';
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

interface UploadResult {
  key: string;
  fileUrl: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

async function uploadFile(file: File, folder: string): Promise<UploadResult> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await api.post('/uploads', fd, {
    params: { folder },
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

interface DocFormState {
  title: string;
  description: string;
  category: string;
  entityType: string;
  entityId: string;
  tags: string;
  fileUrl: string;
  fileName: string;
}

const emptyForm: DocFormState = {
  title: '',
  description: '',
  category: 'OTHER',
  entityType: '',
  entityId: '',
  tags: '',
  fileUrl: '',
  fileName: '',
};

export function DocumentsPage() {
  const { t } = useTranslation('documents');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('documents:create') || hasPermission('documents:*');
  const canUpdate = hasPermission('documents:update') || hasPermission('documents:*');
  const canDelete = hasPermission('documents:delete') || hasPermission('documents:*');

  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<DocFormState>(emptyForm);
  const [editDoc, setEditDoc] = useState<DocumentItem | null>(null);
  const [versionDoc, setVersionDoc] = useState<DocumentItem | null>(null);
  const [historyDoc, setHistoryDoc] = useState<DocumentItem | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [versionFile, setVersionFile] = useState<File | null>(null);

  const { data, isLoading } = useQuery<DocumentListResponse>({
    queryKey: ['documents', page, search, categoryFilter],
    queryFn: async () => {
      const res = await api.get('/documents', {
        params: { page, limit: 20, search: search || undefined, category: categoryFilter || undefined },
      });
      return res.data;
    },
  });

  const { data: versions } = useQuery<DocumentItem[]>({
    queryKey: ['document-versions', historyDoc?.id],
    queryFn: async () => {
      const res = await api.get(`/documents/${historyDoc!.id}/versions`);
      return res.data;
    },
    enabled: !!historyDoc,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['documents'] });

  const createMutation = useMutation({
    mutationFn: async (payload: { form: DocFormState; file: File }) => {
      const uploaded = await uploadFile(payload.file, 'documents');
      const res = await api.post('/documents', {
        title: payload.form.title,
        description: payload.form.description || undefined,
        category: payload.form.category || 'OTHER',
        entityType: payload.form.entityType || undefined,
        entityId: payload.form.entityId || undefined,
        tags: payload.form.tags || undefined,
        fileUrl: uploaded.key,
        storageKey: uploaded.key,
        fileName: uploaded.fileName,
        mimeType: uploaded.mimeType,
        fileSize: uploaded.fileSize,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('toast.uploaded'));
      setShowCreate(false);
      setForm(emptyForm);
      setSelectedFile(null);
      invalidate();
    },
    onError: () => toast.error(t('toast.uploadFailed')),
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; data: DocFormState }) => {
      const res = await api.patch(`/documents/${payload.id}`, {
        title: payload.data.title,
        description: payload.data.description || undefined,
        category: payload.data.category,
        tags: payload.data.tags || undefined,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('toast.updated'));
      setEditDoc(null);
      invalidate();
    },
    onError: () => toast.error(t('toast.updateFailed')),
  });

  const versionMutation = useMutation({
    mutationFn: async (payload: { id: string; file: File }) => {
      const uploaded = await uploadFile(payload.file, 'documents');
      const res = await api.post(`/documents/${payload.id}/versions`, {
        fileUrl: uploaded.key,
        storageKey: uploaded.key,
        fileName: uploaded.fileName,
        mimeType: uploaded.mimeType,
        fileSize: uploaded.fileSize,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('toast.versionUploaded'));
      setVersionDoc(null);
      setVersionFile(null);
      invalidate();
    },
    onError: () => toast.error(t('toast.versionFailed')),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/documents/${id}`);
    },
    onSuccess: () => {
      toast.success('Document deleted');
      setConfirmDeleteId(null);
      invalidate();
    },
    onError: () => toast.error('Failed to delete document'),
  });

  const documents = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  const openEdit = (doc: DocumentItem) => {
    setEditDoc(doc);
    setForm({
      title: doc.title,
      description: doc.description ?? '',
      category: doc.category,
      entityType: doc.entityType ?? '',
      entityId: doc.entityId ?? '',
      tags: doc.tags ?? '',
      fileUrl: doc.fileUrl,
      fileName: doc.fileName ?? '',
    });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Document Management"
        subtitle="Central repository for contracts, CNICs, invoices and supporting files"
        actions={
          canCreate ? (
            <Button onClick={() => { setForm(emptyForm); setSelectedFile(null); setShowCreate(true); }}>
              <Plus className="h-4 w-4" /> Upload Document
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 py-4">
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearch(searchInput);
            }}
          >
            <div className="relative">
              <Search className="absolute start-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 w-auto ps-8 pe-3"
                placeholder="Search documents..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Button type="submit" variant="outline">Search</Button>
          </form>
          <Dropdown
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.value ?? ''); setPage(1); }}
            options={[{ label: 'All Categories', value: '' }, ...CATEGORIES.map((c) => ({ label: c, value: c }))]}
            placeholder="All Categories"
            className="h-9"
          />
        </CardContent>
      </Card>

      {isLoading ? (
        <SkeletonTable rows={6} cols={5} />
      ) : documents.length === 0 ? (
        <EmptyState icon={FileText} title="No documents" subtitle="Upload a document to get started." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('tbl.title')}</Th>
                  <Th>{t('fields.category')}</Th>
                  <Th>{t('tbl.linkedTo')}</Th>
                  <Th>{t('tbl.version')}</Th>
                  <Th>{t('tbl.updated')}</Th>
                  <Th className="text-end">{t('tbl.actions')}</Th>
                </Tr>
              </THead>
              <TBody>
                {documents.map((doc) => (
                  <Tr key={doc.id}>
                    <Td label={t('tbl.title')} className="td-name">
                      <div className="font-medium text-foreground">{doc.title}</div>
                      {doc.tags && <div className="text-xs text-muted-foreground">{doc.tags}</div>}
                    </Td>
                    <Td label={t('fields.category')}>{doc.category}</Td>
                    <Td label={t('tbl.linkedTo')}>{doc.entityType ?? '—'}</Td>
                    <Td label={t('tbl.version')}>v{doc.version}</Td>
                    <Td label={t('tbl.updated')}>{formatDate(doc.updatedAt)}</Td>
                    <Td label={t('tbl.actions')} className="td-actions">
                      <TableRowActions
                        actions={[
                          {
                            key: 'open',
                            label: 'Open file',
                            icon: ExternalLink,
                            onSelect: () => openStoredObject(doc.fileUrl),
                            primary: true,
                          },
                          {
                            key: 'history',
                            label: 'Version history',
                            icon: History,
                            onSelect: () => setHistoryDoc(doc),
                            primary: true,
                          },
                          {
                            key: 'edit',
                            label: 'Edit metadata',
                            icon: Pencil,
                            onSelect: () => openEdit(doc),
                            permission: canUpdate,
                          },
                          {
                            key: 'version',
                            label: 'Upload new version',
                            icon: UploadCloud,
                            onSelect: () => { setVersionDoc(doc); setVersionFile(null); },
                            permission: canUpdate,
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            icon: Trash2,
                            onSelect: () => setConfirmDeleteId(doc.id),
                            permission: canDelete,
                            variant: 'destructive',
                          },
                        ]}
                      />
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}

      {/* Create / Edit dialog */}
      <FormDialog
        open={showCreate || !!editDoc}
        title={editDoc ? t('dialog.editTitle') : t('dialog.uploadTitle')}
        width="32rem"
        submitLabel={editDoc ? t('dialog.save') : t('dialog.upload')}
        isPending={createMutation.isPending || updateMutation.isPending}
        onClose={() => { setShowCreate(false); setEditDoc(null); setSelectedFile(null); }}
        onSubmit={(e) => {
          e.preventDefault();
          if (editDoc) {
            updateMutation.mutate({ id: editDoc.id, data: form });
          } else if (selectedFile) {
            createMutation.mutate({ form, file: selectedFile });
          } else {
            toast.error(t('toast.chooseFile'));
          }
        }}
      >
          <div>
            <Label className="mb-1 block">{t('fields.title')}</Label>
            <Input
              required
              className="h-9"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div>
            <Label className="mb-1 block">{t('fields.category')}</Label>
            <Dropdown
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.value })}
              options={CATEGORIES.map((c) => ({ label: t(`categories.${c}`), value: c }))}
              className="w-full"
            />
          </div>
          {!editDoc && (
            <>
              <div>
                <Label className="mb-1 block">{t('fields.linkedEntityType')}</Label>
                <Dropdown
                  value={form.entityType}
                  onChange={(e) => setForm({ ...form, entityType: e.value ?? '' })}
                  options={[{ label: t('fields.none'), value: '' }, ...ENTITY_TYPES.map((et) => ({ label: t(`entityTypes.${et}`), value: et }))]}
                  placeholder={t('fields.none')}
                  className="w-full"
                />
              </div>
              <div>
                <Label className="mb-1 block">{t('fields.linkedEntityId')}</Label>
                <Input
                  className="h-9"
                  value={form.entityId}
                  onChange={(e) => setForm({ ...form, entityId: e.target.value })}
                />
              </div>
              <div>
                <Label className="mb-1 block">{t('fields.file')}</Label>
                <input
                  required
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  className="block w-full text-sm text-foreground file:me-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:text-primary-foreground hover:file:cursor-pointer"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    if (f && f.size > MAX_FILE_SIZE) {
                      toast.error(t('toast.fileTooLarge'));
                      e.target.value = '';
                      setSelectedFile(null);
                      return;
                    }
                    setSelectedFile(f);
                  }}
                />
                {selectedFile && (
                  <p className="mt-1 text-xs text-muted-foreground">{selectedFile.name}</p>
                )}
              </div>
            </>
          )}
          <div>
            <Label className="mb-1 block">{t('fields.tags')}</Label>
            <Input
              className="h-9"
              placeholder={t('placeholders.tags')}
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
            />
          </div>
          <div>
            <Label className="mb-1 block">{t('fields.description')}</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
      </FormDialog>

      {/* New version dialog */}
      <FormDialog
        open={!!versionDoc}
        title={t('dialog.versionTitle', { title: versionDoc?.title ?? '' })}
        width="28rem"
        submitLabel={t('dialog.uploadVersion')}
        isPending={versionMutation.isPending}
        onClose={() => { setVersionDoc(null); setVersionFile(null); }}
        onSubmit={(e) => {
          e.preventDefault();
          if (versionDoc && versionFile) {
            versionMutation.mutate({ id: versionDoc.id, file: versionFile });
          } else {
            toast.error(t('toast.chooseFile'));
          }
        }}
      >
          <div>
            <Label className="mb-1 block">{t('fields.newFile')}</Label>
            <input
              required
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              className="block w-full text-sm text-foreground file:me-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:text-primary-foreground hover:file:cursor-pointer"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                if (f && f.size > MAX_FILE_SIZE) {
                  toast.error(t('toast.fileTooLarge'));
                  e.target.value = '';
                  setVersionFile(null);
                  return;
                }
                setVersionFile(f);
              }}
            />
            {versionFile && <p className="mt-1 text-xs text-muted-foreground">{versionFile.name}</p>}
          </div>
      </FormDialog>

      {/* Version history dialog */}
      <Dialog
        header={`Version History — ${historyDoc?.title ?? ''}`}
        visible={!!historyDoc}
        onHide={() => setHistoryDoc(null)}
        style={{ width: '32rem' }}
      >
        <div className="pt-2">
          {(versions ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No versions found.</p>
          ) : (
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('tbl.version')}</Th>
                  <Th>{t('tbl.file')}</Th>
                  <Th>{t('tbl.date')}</Th>
                  <Th><span className="sr-only">{t('tbl.actions')}</span></Th>
                </Tr>
              </THead>
              <TBody>
                {(versions ?? []).map((v) => (
                  <Tr key={v.id}>
                    <Td label={t('tbl.version')}>
                      v{v.version} {v.isLatest && <span className="text-xs text-primary-text">(latest)</span>}
                    </Td>
                    <Td label={t('tbl.file')}>{v.fileName ?? '—'}</Td>
                    <Td label={t('tbl.date')}>{formatDate(v.createdAt)}</Td>
                    <Td label={t('tbl.actions')} className="td-actions text-end">
                      <Button size="sm" variant="ghost" onClick={() => openStoredObject(v.fileUrl)}>
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteId}
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={() => confirmDeleteId && deleteMutation.mutate(confirmDeleteId)}
        title="Delete Document"
        message="Are you sure you want to delete this document and all its versions? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
